import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  message,
  Space,
  Input,
  Tag,
  Typography,
  Card,
  Empty,
  Spin,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import api from "../api";

const { Title } = Typography;


const AttendanceList = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch both datasets
      const [attendRes, preConfirmRes] = await Promise.all([
        api.get("/member/geteventattendance"),
        api.get("/member/geteventpreconfirm")
      ]);

      const attendData = attendRes.data.data || attendRes.data || [];
      const preData = preConfirmRes.data.data || preConfirmRes.data || [];

      // Merge data by eventId
      // We will create a map where key is eventId
      const eventMap = {};

      // Process Pre-confirmations first
      preData.forEach(item => {
        const eid = item.eventid?._id || item.eventid;
        if (!eid) return;
        if (!eventMap[eid]) {
          eventMap[eid] = { 
            eventid: item.eventid, 
            preConfirmMembers: item.memberid || [], 
            preConfirmGuests: item.nonmember || [],
            attendanceMembers: [],
            attendanceGuests: [],
            attendanceId: null,
            preConfirmId: item._id
          };
        } else {
          eventMap[eid].preConfirmMembers = item.memberid || [];
          eventMap[eid].preConfirmGuests = item.nonmember || [];
          eventMap[eid].preConfirmId = item._id;
        }
      });

      // Process Attendance
      attendData.forEach(item => {
        const eid = item.eventid?._id || item.eventid;
        if (!eid) return;
        if (!eventMap[eid]) {
          eventMap[eid] = {
            eventid: item.eventid,
            preConfirmMembers: [],
            preConfirmGuests: [],
            attendanceMembers: item.memberid || [],
            attendanceGuests: item.nonmember || [],
            attendanceId: item._id,
            preConfirmId: null
          };
        } else {
          eventMap[eid].attendanceMembers = item.memberid || [];
          eventMap[eid].attendanceGuests = item.nonmember || [];
          eventMap[eid].attendanceId = item._id;
        }
      });

      const processedData = Object.values(eventMap).sort((a, b) => {
        const dateA = new Date(a.eventid?.eventDate || 0);
        const dateB = new Date(b.eventid?.eventDate || 0);
        return dateB - dateA;
      });

      setData(processedData);
      setFilteredData(processedData);
      message.success("Data loaded and unified successfully");
    } catch (err) {
      console.error("Fetch Error:", err);
      message.error("Failed to load records");
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async (record) => {
    try {
      if (record.attendanceId) await api.delete(`/member/deleteeventattendance/${record.attendanceId}`);
      if (record.preConfirmId) await api.delete(`/member/deleteeventpreconfirm/${record.preConfirmId}`);
      message.success("Records deleted successfully");
      fetchData();
    } catch (err) {
      console.error("Delete Error:", err);
      message.error("Failed to delete record");
    }
  };


  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredData(data);
      return;
    }

    const lowerValue = value.toLowerCase();
    const filtered = data.filter((item) => {
      const eventName = item.eventid?.eventName || "";
      const members = [
        ...(item.preConfirmMembers || []),
        ...(item.attendanceMembers || [])
      ].map(m => m.Name || "").join(" ");
      
      const guests = [
        ...(item.preConfirmGuests || []),
        ...(item.attendanceGuests || [])
      ].map(g => g.name || "").join(" ");

      return (
        eventName.toLowerCase().includes(lowerValue) ||
        members.toLowerCase().includes(lowerValue) ||
        guests.toLowerCase().includes(lowerValue)
      );
    });
    setFilteredData(filtered);
  };


  // Function to open modal with full list
  const showFullList = (title, items) => {
    setModalTitle(title);
    setModalContent(items);
    setModalVisible(true);
  };

  const exportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      message.warning("No data available to export");
      return;
    }

    const excelData = [];
    let sNo = 1;

    filteredData.forEach((record) => {
      const eventName = record.eventid?.eventName || "N/A";
      const eventDate = record.eventid?.eventDate
        ? new Date(record.eventid.eventDate).toLocaleDateString("en-IN")
        : "N/A";
      
      // Collect all unique member IDs involved
      const allMemberIds = new Set([
        ...record.preConfirmMembers.map(m => m._id),
        ...record.attendanceMembers.map(m => m._id)
      ]);

      const memberMap = {};
      [...record.preConfirmMembers, ...record.attendanceMembers].forEach(m => {
        if (m && m._id) memberMap[m._id] = m;
      });

      // Add Members with Status
      Array.from(allMemberIds).filter(id => id).forEach(id => {
        const member = memberMap[id];
        const pre = record.preConfirmMembers.some(m => m._id === id) ? "YES" : "NO";
        const att = record.attendanceMembers.some(m => m._id === id) ? "YES" : "NO";

        excelData.push({
          "S.No": sNo++,
          "Event Name": eventName,
          "Event Date": eventDate,
          "Attendee Type": "Member",
          "ACCI Code": member.Accno || "N/A",
          Name: member.Name || "Unknown",
          "Mobile": member.Mobileno || "N/A",
          "Pre-Confirmed": pre,
          "Attended": att,
          "Status": att === "YES" ? "Present" : "Absent",
        });
      });

      // Collect all unique Guest phones involved
      const guestPhones = new Set([
        ...record.preConfirmGuests.map(g => g.mobilenumber || g.phone),
        ...record.attendanceGuests.map(g => g.mobilenumber || g.phone)
      ]);

      const guestMap = {};
      [...record.preConfirmGuests, ...record.attendanceGuests].forEach(g => {
        const phone = g.mobilenumber || g.phone;
        if (phone) guestMap[phone] = g;
      });

      // Add Guests with Status
      Array.from(guestPhones).filter(p => p).forEach(phone => {
        const guest = guestMap[phone];
        const pre = record.preConfirmGuests.some(g => (g.mobilenumber || g.phone) === phone) ? "YES" : "NO";
        const att = record.attendanceGuests.some(g => (g.mobilenumber || g.phone) === phone) ? "YES" : "NO";

        excelData.push({
          "S.No": sNo++,
          "Event Name": eventName,
          "Event Date": eventDate,
          "Attendee Type": "Guest",
          "ACCI Code": "-",
          Name: guest.name || "Unknown",
          "Mobile": phone,
          "Pre-Confirmed": pre,
          "Attended": att,
          "Status": att === "YES" ? "Present" : "Absent",
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Event Summary");
    XLSX.writeFile(workbook, `Attendance_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
    message.success("Excel report downloaded successfully!");
  };


  const columns = [
    {
      title: "Event",
      dataIndex: "eventid",
      key: "event",
      render: (eventid) => (
        <div>
          <div style={{ fontWeight: "500" }}>{eventid?.eventName || "N/A"}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            {eventid?.eventDate
              ? new Date(eventid.eventDate).toLocaleDateString("en-IN")
              : "N/A"}
          </div>
        </div>
      ),
      sorter: (a, b) =>
        (a.eventid?.eventName || "").localeCompare(b.eventid?.eventName || ""),
    },
    {
      title: "Stats",
      key: "stats",
      responsive: ["md"],
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <div>
            <Tag color="orange" style={{ width: '120px', textAlign: 'center' }}>
              Pre: {record.preConfirmMembers.length + record.preConfirmGuests.length}
            </Tag>
          </div>
          <div>
            <Tag color="blue" style={{ width: '120px', textAlign: 'center' }}>
              Att: {record.attendanceMembers.length + record.attendanceGuests.length}
            </Tag>
          </div>
        </Space>
      ),
    },

    {
      title: "Pre-Confirmed",
      key: "preconfirmed",
      responsive: ["lg"],
      render: (_, record) => {
        const all = [...record.preConfirmMembers, ...record.preConfirmGuests];
        const display = all.slice(0, 3);
        return (
          <Space wrap>
            {display.map((m, i) => <Tag color="orange" key={i}>{m.Name || m.name}</Tag>)}
            {all.length > 3 && <span style={{ fontSize: '11px' }}>+{all.length - 3}</span>}
          </Space>
        );
      }
    },
    {
      title: "Attended",
      key: "attended",
      responsive: ["lg"],
      render: (_, record) => {
        const all = [...record.attendanceMembers, ...record.attendanceGuests];
        const display = all.slice(0, 3);
        return (
          <Space wrap>
            {display.map((m, i) => <Tag color="blue" key={i}>{m.Name || m.name}</Tag>)}
            {all.length > 3 && <span style={{ fontSize: '11px' }}>+{all.length - 3}</span>}
          </Space>
        );
      }
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record)}
          size="small"
        >
          Delete
        </Button>
      ),
    },

  ];

  // Mobile card view with updated data structure
  const mobileCards = filteredData.map((record) => {
    const eid = record.eventid?._id || Math.random();
    const preCount = (record.preConfirmMembers?.length || 0) + (record.preConfirmGuests?.length || 0);
    const attCount = (record.attendanceMembers?.length || 0) + (record.attendanceGuests?.length || 0);

    return (
      <Card key={eid} style={{ marginBottom: 16 }} size="small">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div style={{ flex: 1 }}>
            <Title level={5} style={{ margin: "0 0 8px 0" }}>
              {record.eventid?.eventName || "N/A"}
            </Title>
            <div style={{ color: "#666", fontSize: "14px", marginBottom: "12px" }}>
              📅{" "}
              {record.eventid?.eventDate
                ? new Date(record.eventid.eventDate).toLocaleDateString("en-IN")
                : "N/A"}
            </div>

            <Space direction="vertical" size={8} style={{ width: "100%" }}>
               <Tag color="orange">Pre-confirmed: {preCount}</Tag>
               <Tag color="blue">Attended: {attCount}</Tag>
            </Space>
          </div>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </div>
      </Card>
    );
  });


  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      <Title level={2} style={{ marginBottom: "24px" }}>
        Event Attendance Records
      </Title>

      <div style={{ marginBottom: "24px", display: "flex", flexWrap: "wrap", gap: "12px" }}>
        <Input.Search
          placeholder="Search by event, member, or guest..."
          allowClear
          enterButton="Search"
          size="large"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          style={{ flex: "1 1 300px", maxWidth: "400px" }}
        />

        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={exportToExcel}
            style={{ color: "#1d7422", borderColor: "#1d7422" }}
          >
            Export Excel
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/dashboard/attendance")}
            size="large"
          >
            New Attendance
          </Button>
        </Space>
      </div>

      {/* Desktop Table */}
      <div className="desktop-view">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey={(record) => record.eventid?._id || Math.random()}
          loading={{ spinning: loading, indicator: <Spin size="large" /> }}

          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          }}
          scroll={{ x: 800 }}
          locale={{ emptyText: <Empty description="No attendance records found" /> }}
        />
      </div>

      {/* Mobile View */}
      <div className="mobile-view" style={{ display: "none" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : filteredData.length === 0 ? (
          <Empty description="No attendance records found" />
        ) : (
          mobileCards
        )}
      </div>

      {/* Modal for Full List */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {modalContent.map((item, index) => (
            <Tag key={index} color="default" style={{ marginBottom: "8px" }}>
              {item}
            </Tag>
          ))}
          {modalContent.length === 0 && <p>No data available</p>}
        </Space>
      </Modal>

      <style jsx>{`
        @media (max-width: 768px) {
          .desktop-view {
            display: none !important;
          }
          .mobile-view {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AttendanceList;